import React from 'react';
import {
  X,
  User,
  MapPin,
  FileText,
  Calculator,
  Calendar,
  Edit,
  Mail,
  Phone,
  Building,
  CreditCard,
  Shield,
  Briefcase,
  Camera
} from 'lucide-react';
import { Timestamp } from 'firebase/firestore';

const ViewEmployeeModal = ({ employee, appointments, clients, roles, onClose, onEdit }) => {
  if (!employee) return null;

  // Fonction pour obtenir le nom du rôle à partir de l'ID
  const getRoleName = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    return role ? role.name : roleId;
  };

  // Trouver les rendez-vous de cet intervenant
  const employeeAppointments = appointments
    .filter(apt => apt.employeeId === employee.id)
    .sort((a, b) => {
      const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date);
      const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date);
      return dateB - dateA; // Du plus récent au plus ancien
    })
    .slice(0, 5); // Limiter aux 5 derniers

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 dark:bg-opacity-70">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* En-tête du modal */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Détails de l'intervenant</h3>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Informations complètes et activités récentes</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <X size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Corps du modal */}
        <div className="p-6">
          {/* Informations principales */}
          <div className="mb-6">
            <h4 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white mb-4">
              <User size={16} className="mr-2 text-blue-600 dark:text-blue-400" />
              Informations principales
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="w-24 h-24 rounded-full border-2 border-blue-300 dark:border-blue-600 flex items-center justify-center overflow-hidden mx-auto mb-3 bg-blue-100 dark:bg-blue-900/20">
                  {employee.photoURL ? (
                    <img
                      src={employee.photoURL}
                      alt={`${employee.firstName} ${employee.lastName}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-blue-600 dark:text-blue-400 font-semibold text-2xl">
                      {employee.firstName ? employee.firstName.charAt(0).toUpperCase() : 'U'}
                    </span>
                  )}
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white">{employee.firstName} {employee.lastName}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{getRoleName(employee.role)}</p>
                <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mt-2 ${
                  employee.isArchived
                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                    : (employee.isActive !== false
                        ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300'
                        : 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300')
                }`}>
                  {employee.isArchived ? 'Archivé' : (employee.isActive !== false ? 'Actif' : 'Inactif')}
                </div>
              </div>

              <div className="md:col-span-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                    <p className="text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2 text-sm">{employee.email || 'Non renseigné'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Téléphone</label>
                    <p className="text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2 text-sm">{employee.phoneNumber || 'Non renseigné'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date de naissance</label>
                    <p className="text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2 text-sm">
                      {employee.birthDate ? (
                        employee.birthDate.toDate ?
                          employee.birthDate.toDate().toLocaleDateString('fr-FR') :
                          new Date(employee.birthDate).toLocaleDateString('fr-FR')
                      ) : 'Non renseignée'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date d'embauche</label>
                    <p className="text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2 text-sm">
                      {employee.hireDate ? (
                        employee.hireDate.toDate ?
                          employee.hireDate.toDate().toLocaleDateString('fr-FR') :
                          new Date(employee.hireDate).toLocaleDateString('fr-FR')
                      ) : 'Non renseignée'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Informations professionnelles */}
          <div className="mb-6">
            <h4 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white mb-4">
              <FileText size={16} className="mr-2 text-blue-600 dark:text-blue-400" />
              Informations professionnelles
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type de contrat</label>
                <p className="text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2 text-sm">
                  {employee.professionalInfo?.contractType || employee.contractType || 'Non renseigné'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Zone d'intervention</label>
                <p className="text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2 text-sm">
                  {employee.defaultRadius ? `${employee.defaultRadius} km` : 'Non renseignée'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rôle</label>
                <p className="text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2 text-sm">
                  {getRoleName(employee.role)}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Numéro de sécurité sociale</label>
                <p className="text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2 text-sm font-mono">
                  {employee.socialSecurity || 'Non renseigné'}
                </p>
              </div>
            </div>
          </div>

          {/* Adresse */}
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <h4 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white mb-4">
              <MapPin size={16} className="mr-2 text-blue-600 dark:text-blue-400" />
              Adresse
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rue</label>
                <p className="text-gray-900 dark:text-white bg-white dark:bg-gray-700 rounded-lg px-3 py-2 text-sm">
                  {employee.address?.street || 'Non renseignée'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Code postal</label>
                <p className="text-gray-900 dark:text-white bg-white dark:bg-gray-700 rounded-lg px-3 py-2 text-sm">
                  {employee.address?.postalCode || 'Non renseigné'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ville</label>
                <p className="text-gray-900 dark:text-white bg-white dark:bg-gray-700 rounded-lg px-3 py-2 text-sm">
                  {employee.address?.city || 'Non renseignée'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Pays</label>
                <p className="text-gray-900 dark:text-white bg-white dark:bg-gray-700 rounded-lg px-3 py-2 text-sm">
                  {employee.address?.country || 'Non renseigné'}
                </p>
              </div>
            </div>
          </div>

          {/* Auto-entrepreneur */}
          <div className="mb-6">
            <h4 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white mb-4">
              <Calculator size={16} className="mr-2 text-blue-600 dark:text-blue-400" />
              Auto-entrepreneur
            </h4>
            <div className="mb-4">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {employee.isAutoEntrepreneur ? '✓ Auto-entrepreneur' : '✗ Pas auto-entrepreneur'}
              </p>
            </div>

            {employee.isAutoEntrepreneur && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Taux horaire</label>
                  <p className="text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2 text-sm">
                    {employee.hourlyRate ? `${employee.hourlyRate} €/h` : 'Non renseigné'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Forfait base</label>
                  <p className="text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2 text-sm">
                    {employee.deplacementRate?.base ? `${employee.deplacementRate.base} €` : 'Non renseigné'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Distance incluse</label>
                  <p className="text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2 text-sm">
                    {employee.deplacementRate?.includedDistance ? `${employee.deplacementRate.includedDistance} km` : 'Non renseigné'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tarif/km</label>
                  <p className="text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2 text-sm">
                    {employee.deplacementRate?.perKm ? `${employee.deplacementRate.perKm} €/km` : 'Non renseigné'}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Informations bancaires */}
          <div className="mb-6">
            <h4 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white mb-4">
              <CreditCard size={16} className="mr-2 text-blue-600 dark:text-blue-400" />
              Informations bancaires
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">IBAN</label>
                <p className="text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2 text-sm font-mono">
                  {employee.bankInfo?.iban || employee.iban || 'Non renseigné'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">BIC</label>
                <p className="text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2 text-sm font-mono">
                  {employee.bankInfo?.bic || employee.bic || 'Non renseigné'}
                </p>
              </div>
            </div>
          </div>

          {/* Statut */}
          <div className="mb-6">
            <h4 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white mb-4">
              <Shield size={16} className="mr-2 text-blue-600 dark:text-blue-400" />
              Statut
            </h4>
            <div className="flex items-center">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {employee.isActive !== false ? '✓ Intervenant actif' : '✗ Intervenant inactif'}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
            >
              Fermer
            </button>
            <button
              onClick={onEdit}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center"
            >
              <Edit size={16} className="mr-2" />
              Modifier
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewEmployeeModal;
